"use client";

import { useEffect, useState } from "react";
import { devLabel } from "@/lib/dev-labels";
import { RECENT_STORAGE_KEY, WIZARDS } from "./wizards-data";
import styles from "./MagicWandModal.module.css";

/**
 * RecentUsed — Phase 25-AB Task 14.
 *
 * Mockup source: docs/mockups/magic-wand.html (panel 29-A).
 *
 * Reads the 5 most-recent wizard keys from
 * `localStorage["mml.magicwand.recent"]` (a JSON string array). Items
 * are rendered with the wizard's name + key + tag. Clicking a row
 * re-runs the wizard via `onRunWizard`.
 *
 * The component exposes a `version` prop so the parent can force a
 * re-read when it pushes a new wizard key (otherwise we'd miss
 * updates that happened in the same browser session).
 */

export interface RecentUsedProps {
	onRunWizard: (key: string) => void;
	/** Bump this whenever the parent writes a new recent entry. */
	version?: number;
}

export function loadRecent(): string[] {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((k): k is string => typeof k === "string").slice(0, 5);
	} catch {
		return [];
	}
}

export function pushRecent(key: string): string[] {
	if (typeof window === "undefined") return [];
	const current = loadRecent();
	const next = [key, ...current.filter((k) => k !== key)].slice(0, 5);
	try {
		window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
	} catch {
		// ignore quota errors
	}
	return next;
}

export function RecentUsed({ onRunWizard, version = 0 }: RecentUsedProps) {
	const [recent, setRecent] = useState<string[]>([]);

	useEffect(() => {
		setRecent(loadRecent());
	}, [version]);

	if (recent.length === 0) {
		return (
			<div className={styles.rightBlock} {...devLabel("29-A")}>
				<div className={styles.rightTitle}>최근 사용 (0)</div>
				<div
					className={styles.emptyState}
					data-testid="recent-used-empty"
				>
					아직 실행한 마법사 없음
				</div>
			</div>
		);
	}

	return (
		<div className={styles.rightBlock} {...devLabel("29-A")}>
			<div className={styles.rightTitle}>최근 사용 ({recent.length})</div>
			<div className={styles.recentList} data-testid="recent-used">
				{recent.map((k) => {
					const w = WIZARDS.find((x) => x.key === k);
					if (!w) return null;
					return (
						<button
							key={k}
							type="button"
							className={styles.recentRow}
							onClick={() => onRunWizard(k)}
							data-testid={`recent-${k}`}
							style={{
								background: "transparent",
								border: "1px solid rgba(255,255,255,0.04)",
								cursor: "pointer",
								color: "inherit",
								fontFamily: "inherit",
								textAlign: "left",
							}}
						>
							<div className={styles.aiIco} aria-hidden="true">
								{w.key.replace(/^AI-/, "")}
							</div>
							<div style={{ flex: 1 }}>
								<div className={styles.recentName}>{w.name}</div>
								<div className={styles.recentMeta}>
									{w.key} · {w.tag}
								</div>
							</div>
						</button>
					);
				})}
			</div>
		</div>
	);
}
